import * as React from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Scan } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Page } from '@/components/AppShell';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Label,
  LoadingState,
  Select,
} from '@/components/ui/primitives';
import {
  ConfirmButton,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/overlays';
import { cn } from '@/lib/utils';
import { PLACE_ID_CONFLICT_CHOICES } from '@shared/enums.js';

export function DuplicatesPage() {
  const queryClient = useQueryClient();
  const [state, setState] = React.useState<'pending' | 'resolved' | 'all'>('pending');
  const [page, setPage] = React.useState(1);
  const [reviewing, setReviewing] = React.useState<number | null>(null);

  const candidates = useQuery({
    queryKey: ['duplicates', state, page],
    queryFn: () => api.listDuplicates({ state, page, pageSize: 25, sortBy: 'confidence', sortDir: 'desc' }),
    placeholderData: keepPreviousData,
  });
  const counts = useQuery({ queryKey: ['duplicate-counts'], queryFn: api.duplicateCounts });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['duplicates'] });
    queryClient.invalidateQueries({ queryKey: ['duplicate-counts'] });
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const scan = useMutation({
    mutationFn: api.scanDuplicates,
    onSuccess: (result) => {
      invalidate();
      toast.success(
        `Scanned ${result.scanned} leads — ${result.created} new candidates${
          result.skippedSuppressed ? `, ${result.skippedSuppressed} suppressed pairs skipped` : ''
        }`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulk = useMutation({
    mutationFn: (action: 'merge_safe_phone' | 'reject' | 'keep_both_suppress') =>
      api.bulkDuplicates({ action }),
    onSuccess: (result) => {
      invalidate();
      toast.success(
        `Processed ${result.processed}: ${result.merged} merged, ${result.rejected} rejected, ${result.suppressed} suppressed${
          result.blocked.length ? `, ${result.blocked.length} blocked` : ''
        }`,
      );
      result.blocked.slice(0, 3).forEach((b) => toast.warning(`Candidate ${b.candidateId}: ${b.reason}`));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Page
      title="Duplicates"
      description="Exact conflicts from imports and probable matches found by scanning. Nothing is merged without your say-so."
      actions={
        <Button size="sm" loading={scan.isPending} onClick={() => scan.mutate()}>
          <Scan className="h-3.5 w-3.5" />
          Scan for probable duplicates
        </Button>
      }
    >
      <div className="space-y-5 p-5 md:p-6">
        <div className="toolbar">
          <Select value={state} onChange={(e) => { setState(e.target.value as never); setPage(1); }} className="w-40">
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </Select>

          {counts.data && counts.data.pending > 0 && (
            <>
              <ConfirmButton
                title="Merge all safe phone matches?"
                description="Only pairs of existing leads that share an exact phone number are merged. Pairs where both sides are customers are skipped."
                confirmLabel="Merge them"
                destructive={false}
                onConfirm={() => bulk.mutate('merge_safe_phone')}
              >
                <Button variant="outline" size="sm">
                  Merge safe phone matches
                </Button>
              </ConfirmButton>
              <ConfirmButton
                title="Reject every pending candidate?"
                description="They are marked resolved as not-duplicates. Leads are untouched."
                confirmLabel="Reject all"
                onConfirm={() => bulk.mutate('reject')}
              >
                <Button variant="outline" size="sm">
                  Reject all
                </Button>
              </ConfirmButton>
            </>
          )}

          {counts.data && (
            <div className="ml-auto flex flex-wrap gap-1.5">
              {counts.data.byType.map((t) => (
                <Badge key={t.match_type} variant="outline">
                  {t.match_type.replace(/_/g, ' ')}: {t.n}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {candidates.isLoading ? (
          <LoadingState />
        ) : !candidates.data || candidates.data.rows.length === 0 ? (
          <EmptyState
            icon={Copy}
            title={state === 'pending' ? 'No duplicates waiting' : 'Nothing here'}
            description={
              state === 'pending'
                ? 'Imports create candidates when a strong key matches but the identity conflicts. Run a scan to look for probable matches too.'
                : undefined
            }
          />
        ) : (
          <Card>
            <table className="data-table">
              <thead>
                <tr>
                  <th className="min-w-[14rem]">Incoming</th>
                  <th className="min-w-[14rem]">Possible match</th>
                  <th className="w-40">Why</th>
                  <th className="w-24 text-right">Confidence</th>
                  <th className="w-28">State</th>
                  <th className="w-28" />
                </tr>
              </thead>
              <tbody>
                {candidates.data.rows.map((candidate) => (
                  <tr key={candidate.id}>
                    <td className="truncate">{candidate.sourceName}</td>
                    <td>
                      <Link
                        to={`/leads/${candidate.possibleMatchLeadId}`}
                        className="hover:text-primary hover:underline"
                      >
                        {candidate.targetName}
                      </Link>
                    </td>
                    <td>
                      <Badge variant="outline">{candidate.matchType.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="text-right tabular-nums">
                      <span
                        className={cn(
                          candidate.confidence >= 0.9
                            ? 'text-red-300'
                            : candidate.confidence >= 0.8
                              ? 'text-amber-300'
                              : 'text-muted-foreground',
                        )}
                      >
                        {Math.round(candidate.confidence * 100)}%
                      </span>
                    </td>
                    <td>
                      {candidate.state === 'pending' ? (
                        <Badge variant="warning">pending</Badge>
                      ) : (
                        <Badge variant="outline">{candidate.resolution ?? 'resolved'}</Badge>
                      )}
                    </td>
                    <td>
                      {candidate.state === 'pending' && (
                        <Button size="xs" onClick={() => setReviewing(candidate.id)}>
                          Review
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {candidates.data.pageCount > 1 && (
              <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
                <span className="text-muted-foreground">
                  Page {page} of {candidates.data.pageCount} · {candidates.data.total} total
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="xs"
                    disabled={page >= candidates.data.pageCount}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      <ReviewDialog candidateId={reviewing} onClose={() => setReviewing(null)} onDone={invalidate} />
    </Page>
  );
}

function ReviewDialog({
  candidateId,
  onClose,
  onDone,
}: {
  candidateId: number | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [fieldChoices, setFieldChoices] = React.useState<Record<string, 'source' | 'target'>>({});
  const [placeIdChoice, setPlaceIdChoice] = React.useState<string>('separate_location');

  const detail = useQuery({
    queryKey: ['duplicate', candidateId],
    queryFn: () => api.getDuplicate(candidateId as number),
    enabled: candidateId !== null,
  });

  React.useEffect(() => {
    setFieldChoices({});
    setPlaceIdChoice('separate_location');
  }, [candidateId]);

  const resolve = useMutation({
    mutationFn: (action: string) =>
      api.resolveDuplicate(candidateId as number, {
        action,
        placeIdChoice: action === 'insert_anyway' ? placeIdChoice : undefined,
        fieldChoices: action === 'merge' ? fieldChoices : undefined,
      }),
    onSuccess: (result) => {
      onDone();
      onClose();
      toast.success(result.message);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const data = detail.data;
  const isImportRow = data?.source.kind === 'import_row';

  return (
    <Dialog open={candidateId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Review possible duplicate</DialogTitle>
          <DialogDescription>
            {isImportRow
              ? 'This row was held back from import because it conflicts with an existing lead.'
              : 'Two existing leads look like the same business.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {detail.isLoading && <LoadingState />}

          {data && (
            <>
              <div className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2">
                <Label>Why this was flagged</Label>
                <ul className="mt-1 list-inside list-disc text-xs">
                  {data.matchReasons.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              </div>

              {data.bothHaveCustomers && (
                <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-red-300">
                  Both leads have customer records. Merging is blocked — resolve the customer records first so no
                  sale or payment history is lost.
                </div>
              )}

              <div>
                <Label>Field comparison — pick which value survives a merge</Label>
                <div className="mt-1 overflow-hidden rounded border border-border">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="w-36">Field</th>
                        <th>Incoming</th>
                        <th>Existing lead</th>
                        <th className="w-32">Keep</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.comparison
                        .filter((row) => row.sourceValue || row.targetValue)
                        .map((row) => (
                          <tr key={row.field}>
                            <td className="text-muted-foreground">{row.label}</td>
                            <td className={cn('truncate', row.conflict && 'text-amber-300')}>
                              {row.sourceValue ?? '—'}
                            </td>
                            <td className={cn('truncate', row.conflict && 'text-amber-300')}>
                              {row.targetValue ?? '—'}
                            </td>
                            <td>
                              {row.conflict ? (
                                <Select
                                  value={fieldChoices[row.field] ?? 'target'}
                                  onChange={(e) =>
                                    setFieldChoices((c) => ({ ...c, [row.field]: e.target.value as never }))
                                  }
                                  className="h-6 text-2xs"
                                >
                                  <option value="target">Existing</option>
                                  <option value="source">Incoming</option>
                                </Select>
                              ) : (
                                <span className="text-2xs text-muted-foreground">no conflict</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {data.involvesPlaceIdConflict && (
                <div className="rounded border border-primary/40 bg-primary/10 px-3 py-2">
                  <Label>Google Place ID conflict</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Both records claim the same Place ID, and a Place ID can belong to only one active lead.
                    Choose how to resolve it before inserting a separate record.
                  </p>
                  <Field label="Resolution" className="mt-2">
                    <Select value={placeIdChoice} onChange={(e) => setPlaceIdChoice(e.target.value)}>
                      {PLACE_ID_CONFLICT_CHOICES.map((choice) => (
                        <option key={choice} value={choice}>
                          {choice === 'separate_location'
                            ? 'Separate location — link to the existing lead as a branch, Place ID stays with the parent'
                            : choice === 'unrelated_business'
                              ? 'Unrelated business — insert with no Place ID and no parent'
                              : 'Reassign the Place ID — move it to the new lead'}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter className="flex-wrap">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>

          {isImportRow ? (
            <>
              <Button variant="outline" size="sm" onClick={() => resolve.mutate('skip')}>
                Skip this row
              </Button>
              <Button variant="outline" size="sm" onClick={() => resolve.mutate('insert_anyway')}>
                Insert anyway
              </Button>
              <Button size="sm" loading={resolve.isPending} onClick={() => resolve.mutate('approve_update')}>
                Approve update
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => resolve.mutate('keep_both')}>
                Keep both
              </Button>
              <Button variant="outline" size="sm" onClick={() => resolve.mutate('keep_both_suppress')}>
                Keep both & never suggest again
              </Button>
              <Button variant="outline" size="sm" onClick={() => resolve.mutate('separate_location')}>
                Separate location
              </Button>
              <Button
                size="sm"
                loading={resolve.isPending}
                disabled={data?.bothHaveCustomers}
                onClick={() => resolve.mutate('merge')}
              >
                Merge
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
